/**
 * Ported from @chatwoot/utils sla.ts with fixed breached-first ordering.
 *
 * The vendored evaluateSLAStatus picks the "most urgent" SLA by sorting on
 * Math.abs(threshold), so an already-breached SLA (e.g. threshold = -300)
 * loses to an upcoming one (e.g. threshold = +30) and the red/flame state
 * gets hidden behind an amber/alarm pill — the longer the breach, the more
 * hidden it gets. This local copy keeps the exact same input/output contract
 * (snake_case appliedSla fields, returns { type, threshold, icon,
 * isSlaMissed }) with one behavioral change in findMostUrgentSLAStatus:
 * any MISSED SLA (threshold <= 0) outranks any pending one; among missed,
 * most-overdue first; among pending, soonest-deadline first.
 */

/**
 * Calculates the threshold for an SLA based on the current time and the provided threshold.
 * @param {number} timeOffset - The time offset in seconds.
 * @param {number|null} threshold - The threshold in seconds or null if not applicable.
 * @returns {number|null} The calculated threshold in seconds or null if the threshold is null.
 */
const calculateThreshold = (timeOffset, threshold) => {
  // Calculate the time left for the SLA to breach or the time since the SLA has missed
  if (threshold === null) return null;
  const currentTime = Math.floor(Date.now() / 1000);
  return timeOffset + threshold - currentTime;
};

/**
 * Finds the most urgent SLA status.
 * Missed SLAs (threshold <= 0) always outrank pending ones; among missed the
 * most-overdue comes first, among pending the soonest deadline comes first —
 * a plain ascending sort on threshold satisfies all three rules at once.
 * @param {Array} SLAStatuses - An array of SLA status objects.
 * @returns The most urgent SLA status object.
 */
const findMostUrgentSLAStatus = SLAStatuses => {
  SLAStatuses.sort((sla1, sla2) => sla1.threshold - sla2.threshold);
  return SLAStatuses[0];
};

/**
 * Formats the SLA time in a human-readable format.
 * @param {number} seconds - The time in seconds.
 * @returns {string} A formatted string representing the time.
 */
const formatSLATime = seconds => {
  const units = {
    y: 31536000, // 60 * 60 * 24 * 365
    mo: 2592000, // 60 * 60 * 24 * 30
    d: 86400, // 60 * 60 * 24
    h: 3600, // 60 * 60
    m: 60,
  };

  if (seconds < 60) {
    return '1m';
  }

  // we will only show two parts, two max granularity's, h-m, y-d, d-h, m, but no seconds
  const parts = [];

  Object.keys(units).forEach(unit => {
    const value = Math.floor(seconds / units[unit]);
    if (seconds < 60 && parts.length > 0) return;
    if (parts.length === 2) return;
    if (value > 0) {
      parts.push(value + unit);
      seconds -= value * units[unit];
    }
  });
  return parts.join(' ');
};

/**
 * Creates an SLA object based on the type, applied SLA, and chat details.
 * @param {string} type - The type of SLA (FRT, NRT, RT).
 * @param {Object} appliedSla - The applied SLA details (snake_case fields).
 * @param {Object} chat - The chat details.
 * @returns {Object|null} An object containing the SLA status or null if conditions are not met.
 */
const createSLAObject = (type, appliedSla, chat) => {
  const {
    sla_first_response_time_threshold: frtThreshold,
    sla_next_response_time_threshold: nrtThreshold,
    sla_resolution_time_threshold: rtThreshold,
    created_at: createdAt,
  } = appliedSla;

  const {
    first_reply_created_at: firstReplyCreatedAt,
    waiting_since: waitingSince,
    status,
  } = chat;

  const SLATypes = {
    FRT: {
      threshold: calculateThreshold(createdAt, frtThreshold),
      //   Check FRT only if threshold is not null and first reply hasn't been made
      condition:
        frtThreshold !== null &&
        (!firstReplyCreatedAt || firstReplyCreatedAt === 0),
    },
    NRT: {
      threshold: calculateThreshold(waitingSince, nrtThreshold),
      // Check NRT only if threshold is not null, first reply has been made and we are waiting since
      condition:
        nrtThreshold !== null && !!firstReplyCreatedAt && !!waitingSince,
    },
    RT: {
      threshold: calculateThreshold(createdAt, rtThreshold),
      // Check RT only if the conversation is open and threshold is not null
      condition: status === 'open' && rtThreshold !== null,
    },
  };

  const SLAStatus = SLATypes[type];
  return SLAStatus ? { ...SLAStatus, type } : null;
};

/**
 * Evaluates SLA conditions and returns an array of SLA status objects.
 * @param {Object} appliedSla - The applied SLA details.
 * @param {Object} chat - The chat details.
 * @returns {Array} An array of SLA status objects.
 */
const evaluateSLAConditions = (appliedSla, chat) => {
  // Filter out the SLA based on conditions and update the object with the breach status(icon, isSlaMissed)
  const SLATypes = ['FRT', 'NRT', 'RT'];
  return SLATypes.map(type => createSLAObject(type, appliedSla, chat))
    .filter(SLAStatus => !!SLAStatus && SLAStatus.condition)
    .map(SLAStatus => ({
      ...SLAStatus,
      icon: SLAStatus.threshold <= 0 ? 'flame' : 'alarm',
      isSlaMissed: SLAStatus.threshold <= 0,
    }));
};

/**
 * Evaluates the SLA status for a given chat and applied SLA.
 * @param {Object} params - The parameters object.
 * @param {Object} params.appliedSla - The applied SLA details (snake_case fields).
 * @param {Object} params.chat - The chat details.
 * @returns {Object} An object containing the most urgent SLA status.
 */
export const evaluateSLAStatus = ({ appliedSla, chat }) => {
  if (!appliedSla || !chat)
    return { type: '', threshold: '', icon: '', isSlaMissed: false };

  // Filter out the SLA and create the object for each breach
  const SLAStatuses = evaluateSLAConditions(appliedSla, chat);

  // Return the most urgent SLA: any missed SLA outranks any pending one
  const mostUrgent = findMostUrgentSLAStatus(SLAStatuses);
  return mostUrgent
    ? {
        type: mostUrgent.type,
        threshold: formatSLATime(
          mostUrgent.threshold <= 0
            ? -mostUrgent.threshold
            : mostUrgent.threshold
        ),
        icon: mostUrgent.icon,
        isSlaMissed: mostUrgent.isSlaMissed,
      }
    : { type: '', threshold: '', icon: '', isSlaMissed: false };
};
